import React from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { SafeImage } from "@/components/SafeImage";

import { COMPANY } from "@/mock";

export const SectionTitle = ({ eyebrow, title, desc, align = "left" }) => (
  <div className={align === "center" ? "text-center" : ""}>
    {eyebrow ? (
      <div className="text-xs font-bold tracking-widest text-emerald-700 uppercase">{eyebrow}</div>
    ) : null}
    <h2
      className={`mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white ${
        align === "center" ? "mx-auto" : ""
      }`}
    >
      {title}
    </h2>
    {desc ? (
      <p
        className={`mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl ${
          align === "center" ? "mx-auto" : ""
        }`}
      >
        {desc}
      </p>
    ) : null}
  </div>
);

export const StatPill = ({ icon: Icon, title, desc }) => (
  <div className="rounded-2xl border border-border/60 bg-background/70 p-4 shadow-sm backdrop-blur">
    <div className="flex items-start gap-3">
      <div className="rounded-xl bg-emerald-50 p-2 text-emerald-900 border border-emerald-100">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-sm font-extrabold tracking-tight">{title}</div>
        <div className="mt-1 text-xs text-muted-foreground">{desc}</div>
      </div>
    </div>
  </div>
);

export const CategoryCard = ({ c }) => (
  <Card className="group overflow-hidden border-border/60 bg-background/70 shadow-sm transition hover:shadow-md">
    <div className="relative">
      <div className="aspect-[16/10] w-full overflow-hidden">
        <SafeImage
          src={c.image}
          alt={`${c.name} - ${COMPANY.name}`}
          imgClassName="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          className=""
          fallbackLabel={`${c.name} image`}
        />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
      <div className="absolute left-4 bottom-4 right-4">
        <div className="text-white text-lg font-extrabold tracking-tight">{c.name}</div>
        <div className="mt-1 text-white/80 text-xs line-clamp-2">{c.short}</div>
      </div>
    </div>
    <CardContent className="p-4">
      <div className="flex flex-wrap gap-2">
        {c.products.slice(0, 3).map((p) => (
          <Badge key={p.slug} variant="secondary" className="bg-secondary/60">
            {p.name}
          </Badge>
        ))}
      </div>
      <div className="mt-4">
        <Button asChild className="w-full bg-slate-900 text-slate-50 hover:bg-slate-800">
          <Link to={`/products/${c.id}`}>Explore {c.name}</Link>
        </Button>
      </div>
    </CardContent>
  </Card>
);

export const TypicalSpecsNote = () => (
  <div className="rounded-3xl border border-border/60 bg-secondary/20 p-5">
    <div className="text-sm font-semibold">Typical specification ranges</div>
    <p className="mt-2 text-sm text-muted-foreground">
      The tables on product pages show typical ranges. Confirm exact diameter/mesh/coating and standards in your RFQ.
    </p>
  </div>
);
